import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from "react"
import MaterialDetailModal from "../components/MarketPlace/CalculadoraDeMateriales/components/MaterialDetailModal.tsx"
import { getMaterialById } from "../services/materialsService.ts"
import type { MaterialDetail } from "../types/calculadoraDeMateriales.ts"

type MaterialDetailModalContextValue = {
  openMaterialDetail: (materialId: number | string) => void
  closeMaterialDetail: () => void
}

const MaterialDetailModalContext = createContext<MaterialDetailModalContextValue | null>(null)

export function MaterialDetailModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [detail, setDetail] = useState<MaterialDetail | null>(null)
  const [selectedId, setSelectedId] = useState<number | string | null>(null)
  const cacheRef = useRef<Record<string, MaterialDetail>>({})

  const closeMaterialDetail = useCallback(() => {
    setIsOpen(false)
    setLoading(false)
    setError(null)
  }, [])

  const openMaterialDetail = useCallback(async (materialId: number | string) => {
    const key = String(materialId)
    setSelectedId(materialId)
    setIsOpen(true)
    const cached = cacheRef.current[key]
    if (cached) {
      setDetail(cached)
      setError(null)
      setLoading(false)
      return
    }
    setDetail(null)
    setLoading(true)
    setError(null)
    try {
      const d = await getMaterialById(materialId)
      setDetail(d)
      cacheRef.current = { ...cacheRef.current, [key]: d }
    } catch (e: unknown) {
      let msg = "No se pudo obtener el detalle del material."
      const err = e as { message?: string; response?: { data?: { message?: string } } }
      const fromApi = err.response?.data?.message
      if (typeof fromApi === "string" && fromApi.trim()) {
        msg = fromApi.trim()
      } else if (typeof err.message === "string" && err.message.trim()) {
        msg = err.message.trim()
      }
      setError(msg)
    } finally {
      setLoading(false)
    }
  }, [])

  const retryMaterialDetail = useCallback(() => {
    if (selectedId == null) return
    void openMaterialDetail(selectedId)
  }, [selectedId, openMaterialDetail])

  const value = useMemo(
    () => ({
      openMaterialDetail,
      closeMaterialDetail,
    }),
    [openMaterialDetail, closeMaterialDetail]
  )

  return (
    <MaterialDetailModalContext.Provider value={value}>
      <div style={{ width: "100%", minWidth: 0, flex: "1 1 auto" }}>{children}</div>
      <MaterialDetailModal
        isOpen={isOpen}
        onClose={closeMaterialDetail}
        isLoading={loading}
        error={error}
        detail={detail}
        onRetry={retryMaterialDetail}
      />
    </MaterialDetailModalContext.Provider>
  )
}

export function useMaterialDetailModal(): MaterialDetailModalContextValue {
  const ctx = useContext(MaterialDetailModalContext)
  if (ctx == null) {
    throw new Error("useMaterialDetailModal must be used within MaterialDetailModalProvider")
  }
  return ctx
}
